import {
  Box,
  Chip,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { getAmbassadorsListFromSearch } from "../../../apis/ambassadors.api";
import { getObjectiveSuggestion } from "../../../apis/initiatives.api";
import { ChipTextField } from "../../../components/chips/chip-textfield";
import { AutoSelectbox } from "../../../components/selectbox/autoselect";
import { AppTextField } from "../../../components/textfields";
import { ENUM_AUTOSELECT_LIST_TYPES } from "../../../enums";
import { convertToFullName } from "../../../services/utils";
import {
  getInitiativeTypeDisplayLabel,
  INITIATIVE_TYPE_OPTIONS,
} from "../../ambassadors/ambassador-details/initiatives/utils";
import type {
  InitiativeFormErrors,
  InitiativeFormValues,
} from "./form-config";
import { TextareaWithAiSuggestion } from "./textarea-ai-suggestion";

const MIN_SUMMARY_LENGTH_FOR_AI = 10;
const SUMMARY_PLACEHOLDER =
  "We have a project in plan for the fiscal year, with budget allocated, to evaluate solutions that will assess the security posture of SaaS applications.";

type InitiativeFormProps = {
  values: InitiativeFormValues;
  errors: InitiativeFormErrors;
  onChange: (field: keyof InitiativeFormValues, value: any) => void;
  showAmbassadorSelect?: boolean;
  selectedAmbassador?: { id: string; fullName: string } | null;
  onAmbassadorChange?: (ambassador: { id: string; fullName: string } | null) => void;
};

function InitiativeForm({
  values,
  errors,
  onChange,
  showAmbassadorSelect = false,
  selectedAmbassador = null,
  onAmbassadorChange,
}: InitiativeFormProps) {
  const dispatch = useDispatch();
  const [ambassadorOptions, setAmbassadorOptions] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  const handleAmbassadorSearch = async (e) => {
    const text = e?.target?.value;

    if (typeof text === "object") {
      onAmbassadorChange?.(text);
      onChange("ambassadorId", text?.id || "");
      return;
    }

    if (!text || text?.length < 3) {
      setAmbassadorOptions([]);
      if (!text) {
        onAmbassadorChange?.(null);
        onChange("ambassadorId", "");
      }
      return;
    }

    const res: any = await dispatch(getAmbassadorsListFromSearch(text) as any);
    if (res?.success) {
      setAmbassadorOptions(
        res?.data?.map((ambassador) => ({
          fullName: convertToFullName(
            ambassador?.firstName,
            ambassador?.lastName
          ),
          id: ambassador?.id,
        })) || []
      );
    }
  };

  const fetchSuggestion = async (payload: Record<string, unknown>) => {
    setSuggestionLoading(true);
    try {
      const res: any = await dispatch(getObjectiveSuggestion(payload) as any);
      return {
        data:
          res?.data ??
          (res?.description != null
            ? { description: res.description }
            : undefined),
      };
    } finally {
      setSuggestionLoading(false);
    }
  };

  return (
    <Stack gap={2.5} component="form" noValidate>
      {showAmbassadorSelect && (
        <Box>
          <Typography fontSize={14} fontWeight={600} color="#111827" mb={0.5}>
            Ambassador
            <Typography component="span" color="error.main">
              *
            </Typography>
          </Typography>
          <AutoSelectbox
            list={ambassadorOptions}
            inlineLabel
            listProperties={{
              field: "fullName",
              value: "id",
            }}
            listType={ENUM_AUTOSELECT_LIST_TYPES.ARRAY_OF_OBJECT}
            requireOnChange
            onChange={handleAmbassadorSearch}
            value={selectedAmbassador}
            label={"Select Ambassador"}
            error={errors.ambassadorId || ""}
            labelStyle={{
              fontSize: 16,
              fontWeight: 400,
              color: "#000000DE",
            }}
            textfieldStyle={{ height: 22 }}
          />
        </Box>
      )}

      <AppTextField
        id="title"
        name="title"
        label="Title"
        placeholder="What are you looking for?"
        value={values.title}
        error={errors.title}
        onChange={(e) => onChange("title", e.target.value)}
      />

      <Box>
        <Typography fontSize={14} fontWeight={600} color="#111827" mb={0.5}>
          Type
          <Typography component="span" color="error.main">
            *
          </Typography>
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {INITIATIVE_TYPE_OPTIONS.map((option) => {
            const selected = values.type === option.value;
            return (
              <Chip
                key={option.value}
                label={getInitiativeTypeDisplayLabel(option.value)}
                onClick={() => onChange("type", option.value)}
                variant={selected ? "filled" : "outlined"}
                sx={{
                  fontWeight: 600,
                  bgcolor: selected ? "#070639" : "transparent",
                  color: selected ? "#fff" : "#111827",
                  borderColor: selected ? "#070639" : "#E5E7EB",
                  "&:hover": {
                    bgcolor: selected ? "#070639" : "#F3F4F6",
                  },
                }}
              />
            );
          })}
        </Box>
        {errors.type && (
          <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
            {errors.type}
          </FormHelperText>
        )}
      </Box>

      {values.type?.toLowerCase() === "replace tool" && (
        <Box>
          <Typography fontSize={14} fontWeight={600} color="#111827" mb={0.5}>
            Tool you are replacing
            <Typography component="span" color="error.main">
              {" *"}
            </Typography>
          </Typography>
          <ChipTextField
            id="toolName"
            field="toolName"
            label=""
            value={values.toolName}
            error={errors.toolName}
            onChange={(e) => onChange("toolName", e.target.value)}
            inlineLabel
            textfieldStyle={{ height: 27 }}
          />
        </Box>
      )}

      <TextareaWithAiSuggestion
        label="Summary"
        required
        value={values.summary}
        onChange={(nextValue) => onChange("summary", nextValue)}
        fetchSuggestion={fetchSuggestion}
        getSuggestPayload={() => ({ description: values.summary ?? "" })}
        suggestionLoading={suggestionLoading}
        minLengthForAi={MIN_SUMMARY_LENGTH_FOR_AI}
        error={errors.summary}
        placeholder={SUMMARY_PLACEHOLDER}
        rows={8}
        rowsWithSuggestion={2}
      />

      <Box
        border={1}
        borderColor={values.isPublic ? "#41C197" : "#E5E7EB"}
        borderRadius="10px"
        p={2}
      >
        <FormControlLabel
          sx={{
            m: 0,
            width: "100%",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
          labelPlacement="start"
          control={
            <Switch
              checked={values.isPublic}
              onChange={(e) => onChange("isPublic", e.target.checked)}
              color="primary"
            />
          }
          label={
            <Stack gap={0.5} pr={2}>
              <Typography fontSize={14} fontWeight={700} color="#111827">
                Public
              </Typography>
              <Typography fontSize={12} color="#6B7280">
                Vendors can&apos;t discover this initiative or propose solutions.
              </Typography>
            </Stack>
          }
        />
      </Box>
    </Stack>
  );
}

export { InitiativeForm };
